// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  classNames,
  composeEventHandler,
  defineComponent,
  IconButton,
} from '@shared/ui-kit';
import { Tooltip, Typography } from '@mui/material';
import { useMemo, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  formatIbanWithGroups,
  maskIban,
  normalizeIban,
  type IbanMaskFormat,
} from '../../utils';
import styles from './mask-iban.module.css';

export interface UseMaskIbanProps {
  readonly iban: string | null | undefined;
  readonly format?: IbanMaskFormat;
  readonly defaultRevealed?: boolean;
}

function useMaskIban({
  iban,
  format = 'first-and-last',
  defaultRevealed = false,
}: UseMaskIbanProps) {
  const { t } = useTranslation();

  const [revealed, setRevealed] = useState(defaultRevealed);

  const raw = useMemo(() => normalizeIban(iban), [iban]);

  const hasIban = raw.length > 0;

  const displayText = useMemo(
    () => (revealed ? formatIbanWithGroups(iban) : maskIban(iban, format)),
    [revealed, iban, format],
  );

  const onToggleRevealed = useMemo(
    () =>
      composeEventHandler(
        (event: MouseEvent<HTMLButtonElement>) => {
          event.preventDefault();
          if (!hasIban) return;
          setRevealed(v => !v);
        },
        { stopPropagation: true },
      ),
    [hasIban],
  );

  const onCopy = useMemo(
    () =>
      composeEventHandler(
        (event: MouseEvent<HTMLButtonElement>) => {
          event.preventDefault();
          if (!hasIban) return;
          void navigator.clipboard?.writeText(raw);
        },
        { stopPropagation: true },
      ),
    [hasIban, raw],
  );

  const copyTooltipTitle = useMemo(
    () => t('common.iban-copy', { ns: 'common' }),
    [t],
  );

  const toggleTooltipTitle = useMemo(
    () =>
      revealed
        ? t('common.iban-hide', { ns: 'common' })
        : t('common.iban-show', { ns: 'common' }),
    [revealed, t],
  );

  return {
    copyTooltipTitle,
    displayText,
    hasIban,
    onCopy,
    onToggleRevealed,
    revealed,
    toggleTooltipTitle,
  };
}

export const MaskIban = defineComponent({
  displayName: 'MaskIban',
  useSetup: useMaskIban,
  View: ({
    copyTooltipTitle,
    displayText,
    hasIban,
    onCopy,
    onToggleRevealed,
    revealed,
    toggleTooltipTitle,
  }) => (
    <span className={styles.root}>
      <span className={styles.value}>
        <Typography
          variant="body2"
          className={classNames(styles.valueText, '!money-display')}
        >
          {displayText}
        </Typography>
      </span>

      <span className={styles.actions}>
        <Tooltip title={toggleTooltipTitle}>
          <span className="flex items-center">
            <IconButton
              type="button"
              icon={revealed ? 'EyeOff' : 'Eye'}
              variant="icon"
              color="dark"
              size="lg"
              disabled={!hasIban}
              onClick={onToggleRevealed}
              aria-label={toggleTooltipTitle}
            />
          </span>
        </Tooltip>

        <Tooltip title={copyTooltipTitle}>
          <span className="flex items-center">
            <IconButton
              type="button"
              icon="Copy"
              variant="icon"
              color="dark"
              size="lg"
              disabled={!hasIban}
              onClick={onCopy}
              aria-label={copyTooltipTitle}
            />
          </span>
        </Tooltip>
      </span>
    </span>
  ),
});
